import { createStore } from "devextreme-aspnet-data-nojquery";
import { jsPDF as JsPDF } from "jspdf";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import history from "src/utils/history";
import { paths } from "src/routes/paths";
import { groupRowStyle } from "src/theme/palette";
import { refreshToken } from "./token";

export function getDataSourceStore(url, options, filters) {
  options = options || {};

  return createStore({
    key: options.id || "id",
    loadMethod: options.method || "POST",
    loadUrl: url,
    onBeforeSend: async (method, ajaxOptions) => {
      await refreshToken(true, false);

      // ajaxOptions.data.stringToLower = true;

      if (ajaxOptions.data.filter) ajaxOptions.data.filter = JSON.parse(ajaxOptions.data.filter);

      if (ajaxOptions.data.sort) ajaxOptions.data.sort = JSON.parse(ajaxOptions.data.sort);

      if (ajaxOptions.data.group) ajaxOptions.data.group = JSON.parse(ajaxOptions.data.group);

      if (ajaxOptions.data.totalSummary)
        ajaxOptions.data.totalSummary = JSON.parse(ajaxOptions.data.totalSummary);

      if (ajaxOptions.data.groupSummary)
        ajaxOptions.data.groupSummary = JSON.parse(ajaxOptions.data.groupSummary);

      if (ajaxOptions.data.select) ajaxOptions.data.select = JSON.parse(ajaxOptions.data.select);

      if (filters) {
        const data = {
          ...structuredClone(filters),
          loadOptions: structuredClone(ajaxOptions.data),
        };
        ajaxOptions.data = JSON.stringify(data);
      } else {
        ajaxOptions.data = JSON.stringify(structuredClone(ajaxOptions.data));
      }

      ajaxOptions.headers = {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        "Content-Type": "application/json",
        "X-Consuman-TimeZone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    },
    onAjaxError: e => {
      if (e.xhr?.status === 403 || e.xhr?.status === 0) {
        history.push(paths.auth.login);
      }
    },
  });
}

export function getDateCalculateFilterExpression(filterValue, selectedFilterOperation, target) {
  if (selectedFilterOperation === "between" && Array.isArray(filterValue)) {
    const filterExpression = [
      [this.dataField, ">=", filterValue[0] ? filterValue[0].toISOString() : filterValue[0]],
      "and",
      [this.dataField, "<=", filterValue[1] ? filterValue[1].toISOString() : filterValue[1]],
    ];

    return filterExpression;
  }

  return [this.dataField, selectedFilterOperation || "contains", filterValue.toISOString()];
}

const DEFAULT_FILTER_CLEAR_SELECTOR =
  ".dx-datagrid-filter-panel-clear-filter, .dx-treelist-filter-panel-clear-filter";

// Hace que el botón "Limpiar filtros" del panel deje aplicado el filtro por defecto de la tabla. Se
// intercepta SOLO el click de ese botón, con delegación en fase de captura sobre la raíz de la grilla,
// para reemplazar el comportamiento nativo (que pone filterValue en null).
export function installDefaultFilterClear(component, defaultFilterValue) {
  if (!component || !defaultFilterValue || typeof component.element !== "function") return;

  const root = component.element();
  const el = root && typeof root.get === "function" ? root.get(0) : root;
  if (!el || typeof el.addEventListener !== "function" || el.__defaultFilterClearBound) return;
  el.__defaultFilterClearBound = true;

  el.addEventListener(
    "click",
    ev => {
      if (!ev.target || typeof ev.target.closest !== "function") return;
      if (!ev.target.closest(DEFAULT_FILTER_CLEAR_SELECTOR)) return;
      ev.stopImmediatePropagation();
      ev.preventDefault();
      component.option("filterValue", JSON.parse(JSON.stringify(defaultFilterValue)));
      component.option("filterPanel.filterEnabled", true);
    },
    true
  );
}

/**
 * `onSelectionChanged` handler for grids whose selection feeds a removal: an already removed record
 * cannot be removed again. Restored selections (state storing keeps the selected keys) and any path that
 * bypasses the row checkbox are covered here.
 */
export function deselectRemovedRows(e) {
  const removedIds = e.selectedRowsData.filter(row => !row.isActive).map(row => row.id);

  if (removedIds.length > 0) e.component.deselectRows(removedIds);
}

export function exportCallback(e, fileName) {
  if (e.format === "xlsx") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(fileName);

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell(options) {
        const { gridCell, excelCell } = options;

        if (gridCell.column.dataType === "number" && gridCell.value != null) {
          const roundedValue = Math.round(gridCell.value * 100) / 100;
          excelCell.value = roundedValue;
          excelCell.numFmt =
            typeof gridCell.column.format === "string" ? gridCell.column.format : "0.00";
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileName}.xlsx`);
      });
    });
  } else if (e.format === "pdf") {
    const doc = new JsPDF();

    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: e.component,
    }).then(() => {
      doc.save(`${fileName}.pdf`);
    });
  }
}

export function applyGroupRowStyle(rowElement, settings) {
  const { backgroundColor, color } = groupRowStyle(settings);

  rowElement.querySelectorAll("td").forEach(td => {
    td.style.backgroundColor = backgroundColor;
    td.style.color = color;
  });
}
